<script setup lang="ts">
/**
 * A row of cards that never wraps onto a second line: the cards sit side by side and the row scrolls sideways when
 * they do not all fit, snapping to a card. Previous and Next step it for a pointer; the row itself takes focus, so
 * the arrow keys scroll it too. The buttons appear only while there is something to scroll to.
 *
 * It opens on the card carrying `aria-current="true"`, when there is one, so what is selected is never off-screen.
 * Children are the cards themselves, as list items (`as="li"` on a kit card).
 */
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'

defineProps<{
    /** What the row lists, for assistive technology: "Plans". */
    label: string
}>()

const track = ref<HTMLElement | null>(null)
const atStart = ref(true)
const atEnd = ref(true)
let observer: ResizeObserver | null = null

/** Where the row sits in what it can scroll: a pixel of slack, since a snapped row rarely lands on a whole one. */
function measure(): void {
    const row = track.value
    if (!row) {
        return
    }
    atStart.value = row.scrollLeft <= 1
    atEnd.value = row.scrollLeft + row.clientWidth >= row.scrollWidth - 1
}

/** One step is most of what is showing, so the card cut off at the edge is the first one of the next step. */
function step(direction: -1 | 1): void {
    const row = track.value
    if (!row) {
        return
    }
    row.scrollBy?.({ left: direction * row.clientWidth * 0.8, behavior: 'smooth' })
}

onMounted(async () => {
    await nextTick()
    const row = track.value
    if (!row) {
        return
    }
    const current = row.querySelector<HTMLElement>('[aria-current="true"]')
    if (current) {
        // Set on the row, never `scrollIntoView`: that would move the page as well as the row.
        row.scrollLeft = current.offsetLeft - row.offsetLeft
    }
    measure()
    if (typeof ResizeObserver !== 'undefined') {
        observer = new ResizeObserver(measure)
        observer.observe(row)
    }
})

onBeforeUnmount(() => observer?.disconnect())
</script>

<template>
    <div class="zh-carousel">
        <ul ref="track" class="zh-carousel__track" role="list" tabindex="0" :aria-label="label" @scroll.passive="measure">
            <slot />
        </ul>
        <div v-if="!atStart || !atEnd" class="zh-carousel__steps">
            <button type="button" class="zh-carousel__step" :disabled="atStart" :aria-label="`Previous ${label}`" @click="step(-1)">
                <span aria-hidden="true">←</span>
            </button>
            <button type="button" class="zh-carousel__step" :disabled="atEnd" :aria-label="`Next ${label}`" @click="step(1)">
                <span aria-hidden="true">→</span>
            </button>
        </div>
    </div>
</template>

<style scoped>
.zh-carousel {
    display: grid;
    gap: 0.75rem;
    min-width: 0;
}

.zh-carousel__track {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(15rem, 1fr);
    gap: 0.75rem;
    margin: 0;
    padding: 0 0 0.5rem;
    list-style: none;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scroll-snap-type: x proximity;
    scrollbar-width: thin;
    scrollbar-color: var(--line) transparent;
}

.zh-carousel__track > :deep(*) {
    scroll-snap-align: start;
}

.zh-carousel__track:focus-visible {
    outline: 1px solid var(--electric-cyan);
    outline-offset: 2px;
}

.zh-carousel__steps {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
}

.zh-carousel__step {
    display: grid;
    place-items: center;
    width: 2rem;
    height: 2rem;
    border: 1px solid var(--border);
    background: var(--system-charcoal);
    color: var(--ink-1);
    font-family: var(--font-mono);
    cursor: pointer;
    transition: border-color var(--motion-ui) ease;
}

.zh-carousel__step:hover:not(:disabled),
.zh-carousel__step:focus-visible {
    border-color: var(--electric-cyan);
    outline: none;
}

.zh-carousel__step:disabled {
    color: var(--ink-3);
    cursor: default;
}
</style>
